import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Icon,
  LoadingState,
  StatePanel,
  V2Alert,
  V2Badge,
  V2Card,
  V2CardHeader,
  V2DataGrid,
  V2EmptyState,
  V2Kpi,
  V2Tabs,
  type V2GridColumn,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import type {
  ReorderAlertRow,
  ReorderAlertsResponse,
  StagnantStockItem,
  StagnantStockResponse,
  StockBalancesResponse,
} from '../../lib/types/inventory.js';
import {
  REORDER_STATUS_META,
  STOCK_BALANCES_LIMIT,
  formatQuantity,
  isStockBalancesCapped,
  metaOf,
  stateVariantFor,
  stockStatusOf,
} from './inventoryDisplay.js';
import './inventory.css';

type ReportTab = 'warehouses' | 'reorder' | 'stagnant';

const STAGNANT_DAY_OPTIONS = [
  { value: 30, label: '۳۰ روز اخیر' },
  { value: 60, label: '۶۰ روز اخیر' },
  { value: 90, label: '۹۰ روز اخیر' },
  { value: 180, label: '۶ ماه اخیر' },
  { value: 365, label: 'یک سال اخیر' },
] as const;

const fa = (n: number) => n.toLocaleString('fa-IR');

export function InventoryReportsPage() {
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const [activeTab, setActiveTab] = useState<ReportTab>('warehouses');
  const [stagnantDays, setStagnantDays] = useState(60);

  // Tab 1 data
  const balancesResult = useApiGet<StockBalancesResponse>(
    slug && activeTab === 'warehouses'
      ? `${base}/api/v1/inventory/stock-balances?limit=${STOCK_BALANCES_LIMIT}`
      : null,
    [slug, activeTab],
  );

  // Tab 2 data (Reorder Alerts)
  const reorderResult = useApiGet<ReorderAlertsResponse>(
    slug && activeTab === 'reorder' ? `${base}/api/v1/inventory/reports/reorder-alerts` : null,
    [slug, activeTab],
  );

  // Tab 3 data (Stagnant Stock)
  const stagnantResult = useApiGet<StagnantStockResponse>(
    slug && activeTab === 'stagnant'
      ? `${base}/api/v1/inventory/reports/stagnant?days=${stagnantDays}`
      : null,
    [slug, activeTab, stagnantDays],
  );

  if (!slug) return null;

  // Per-warehouse counts. Quantities are never summed here: a warehouse holds products in
  // different units, so a total would add kilograms to pieces (V2_USAGE §4 — "Totals").
  const groups = new Map<
    string,
    { label: string; lines: number; sufficient: number; low: number; out: number }
  >();
  if (balancesResult.status === 'ready') {
    for (const row of balancesResult.data.balances) {
      const group = groups.get(row.warehouseId) ?? {
        label: row.warehouseCode ?? 'انبار نامشخص',
        lines: 0,
        sufficient: 0,
        low: 0,
        out: 0,
      };
      group.lines += 1;
      const status = stockStatusOf(row);
      if (status === 'ناموجود') group.out += 1;
      else if (status === 'کم‌موجود') group.low += 1;
      else group.sufficient += 1;
      groups.set(row.warehouseId, group);
    }
  }
  const totals = [...groups.values()].reduce(
    (acc, g) => ({
      sufficient: acc.sufficient + g.sufficient,
      low: acc.low + g.low,
      out: acc.out + g.out,
    }),
    { sufficient: 0, low: 0, out: 0 },
  );

  // Process reorder alerts metrics
  const counts = { critical: 0, reorder: 0, surplus: 0, normal: 0 };
  if (reorderResult.status === 'ready') {
    for (const r of reorderResult.data.alerts) counts[r.status] += 1;
  }

  const reorderColumns: V2GridColumn<ReorderAlertRow>[] = [
    {
      key: 'product',
      header: 'کالا',
      wrap: true,
      hideable: false,
      render: (row) => (
        <span className="inv-cell">
          <span className="inv-cell__main">{row.productName}</span>
          <span className="inv-cell__sub ltr">{row.productCode}</span>
        </span>
      ),
    },
    { key: 'uom', header: 'واحد', muted: true, render: (row) => row.uomName || '—' },
    {
      key: 'onHand',
      header: 'موجودی کل',
      align: 'end',
      render: (row) => <span className="sk-num">{formatQuantity(row.onHand)}</span>,
    },
    {
      key: 'reserved',
      header: 'رزرو شده',
      align: 'end',
      render: (row) => <span className="sk-num">{formatQuantity(row.reserved)}</span>,
    },
    {
      key: 'available',
      header: 'قابل استفاده',
      align: 'end',
      emphasis: 'key',
      render: (row) => <span className="sk-num">{formatQuantity(row.available)}</span>,
    },
    {
      key: 'min',
      header: 'حداقل',
      align: 'end',
      render: (row) => <span className="sk-num">{formatQuantity(row.minQty)}</span>,
    },
    {
      key: 'rop',
      header: 'نقطهٔ سفارش',
      align: 'end',
      render: (row) => <span className="sk-num">{formatQuantity(row.reorderPoint)}</span>,
    },
    {
      key: 'max',
      header: 'حداکثر',
      align: 'end',
      render: (row) => <span className="sk-num">{formatQuantity(row.maxQty)}</span>,
    },
    {
      key: 'status',
      header: 'وضعیت هشدار',
      render: (row) => {
        const meta = metaOf(REORDER_STATUS_META, row.status);
        return <V2Badge tone={meta.tone}>{meta.label}</V2Badge>;
      },
    },
    {
      key: 'suggested',
      header: 'سفارش پیشنهادی',
      align: 'end',
      render: (row) =>
        row.suggestedOrderQty ? (
          <span className="sk-num inv-strong">+{formatQuantity(row.suggestedOrderQty)}</span>
        ) : (
          <span>—</span>
        ),
    },
    {
      key: 'actions',
      header: 'عملیات',
      hideable: false,
      render: (row) => (
        <Link
          to={`${base}/inventory/cardex?productId=${encodeURIComponent(row.productId)}`}
          className="sk-btn sk-btn--ghost sk-btn--sm inv-btn-link"
        >
          کاردکس
        </Link>
      ),
    },
  ];

  const stagnantColumns: V2GridColumn<StagnantStockItem>[] = [
    {
      key: 'product',
      header: 'کالا',
      wrap: true,
      hideable: false,
      render: (item) => (
        <span className="inv-cell">
          <span className="inv-cell__main">{item.productName}</span>
          <span className="inv-cell__sub ltr">{item.productCode}</span>
        </span>
      ),
    },
    { key: 'uom', header: 'واحد', muted: true, render: (item) => item.uomName || '—' },
    {
      key: 'onHand',
      header: 'موجودی راکد در انبار',
      align: 'end',
      emphasis: 'key',
      render: (item) => <span className="sk-num">{formatQuantity(item.onHand)}</span>,
    },
    {
      key: 'last',
      header: 'تاریخ آخرین تراکنش',
      render: (item) =>
        item.lastMovementDate ? (
          <span className="sk-num">{displayJalaliDate(item.lastMovementDate)}</span>
        ) : (
          <span>فاقد گردش</span>
        ),
    },
    {
      key: 'idle',
      header: 'روزهای بی‌حرکت',
      align: 'end',
      render: (item) => <span className="sk-num inv-tone-warn">{fa(item.daysIdle)} روز</span>,
    },
    {
      key: 'actions',
      header: 'عملیات',
      hideable: false,
      render: (item) => (
        <Link
          to={`${base}/inventory/cardex?productId=${encodeURIComponent(item.productId)}`}
          className="sk-btn sk-btn--secondary sk-btn--sm inv-btn-link"
        >
          کاردکس
        </Link>
      ),
    },
  ];

  const attention = counts.critical + counts.reorder;
  const tabs = [
    { id: 'warehouses', label: 'موجودی به تفکیک انبار' },
    {
      id: 'reorder',
      label: `هشدار نقطهٔ سفارش و کسری کالا${activeTab === 'reorder' && attention > 0 ? ` (${fa(attention)})` : ''}`,
    },
    { id: 'stagnant', label: 'کالاهای راکد و کم‌گردش' },
  ];

  return (
    <div className="inv-page">
      {/* Quick link to Cardex */}
      <V2Alert
        tone="info"
        title="کاردکس مقداری و ریالی کالا"
        action={
          <Link
            to={`${base}/inventory/cardex`}
            className="sk-btn sk-btn--primary sk-btn--sm inv-btn-link"
          >
            <Icon name="invoice" size="sm" />
            <span>مشاهدهٔ کاردکس کالا</span>
          </Link>
        }
      >
        سابقهٔ کامل ورود، خروج و مانده هر کالا، همراه با نرخ‌های میانگین موزون متحرک لحظه‌ای.
      </V2Alert>

      <V2Tabs items={tabs} activeId={activeTab} onChange={(id) => setActiveTab(id as ReportTab)} />

      {/* TAB 1: Stock by Warehouse */}
      {activeTab === 'warehouses' ? (
        <>
          {balancesResult.status === 'loading' ? <LoadingState /> : null}
          {balancesResult.status === 'error' ? (
            <StatePanel
              variant={stateVariantFor(balancesResult.error.status)}
              title={
                balancesResult.error.status === 403 ? 'دسترسی ندارید' : 'گزارش قابل نمایش نیست'
              }
            />
          ) : null}
          {balancesResult.status === 'ready' && groups.size === 0 ? (
            <V2EmptyState
              icon="inventory"
              title="موجودی ثبت نشده"
              description="پس از ثبت اولین رسید یا حواله، موجودی انبارها اینجا نمایش داده می‌شود."
            />
          ) : null}

          {balancesResult.status === 'ready' && groups.size > 0 ? (
            <>
              {isStockBalancesCapped(balancesResult.data.balances.length) ? (
                <V2Alert tone="warn">
                  این گزارش فقط {fa(STOCK_BALANCES_LIMIT)} ردیف موجودی را که اخیراً تغییر کرده‌اند
                  شمارش می‌کند؛ مجموع‌ها ممکن است ناقص باشند.
                </V2Alert>
              ) : null}
              <div className="inv-kpi-grid">
                <V2Kpi label="کافی" value={fa(totals.sufficient)} delta="ردیف موجودی" />
                <div data-tone="warn">
                  <V2Kpi label="دارای رزرو یا مسدودی" value={fa(totals.low)} delta="ردیف موجودی" />
                </div>
                <div data-tone="danger">
                  <V2Kpi label="ناموجود" value={fa(totals.out)} delta="ردیف موجودی" />
                </div>
              </div>

              <div className="inv-card-grid">
                {[...groups.entries()].map(([warehouseId, group]) => (
                  <V2Card key={warehouseId}>
                    <V2CardHeader
                      icon="box"
                      title={group.label}
                      actions={<V2Badge tone="neutral">{fa(group.lines)} ردیف</V2Badge>}
                    />
                    <div className="inv-kv-list">
                      <div className="inv-kv__row">
                        <span className="inv-kv__label">کافی</span>
                        <span className="inv-kv__value sk-num">{fa(group.sufficient)}</span>
                      </div>
                      <div className="inv-kv__row">
                        <span className="inv-kv__label">دارای رزرو یا مسدودی</span>
                        <span className="inv-kv__value sk-num">{fa(group.low)}</span>
                      </div>
                      <div className="inv-kv__row">
                        <span className="inv-kv__label">ناموجود</span>
                        <span
                          className={`inv-kv__value sk-num ${group.out > 0 ? 'inv-tone-down' : ''}`}
                        >
                          {fa(group.out)}
                        </span>
                      </div>
                    </div>
                  </V2Card>
                ))}
              </div>
            </>
          ) : null}
        </>
      ) : null}

      {/* TAB 2: Reorder & Shortage Alerts */}
      {activeTab === 'reorder' ? (
        <>
          {reorderResult.status === 'loading' ? (
            <LoadingState label="در حال محاسبهٔ وضعیت نقاط سفارش و موجودی حداقل/حداکثر…" />
          ) : null}
          {reorderResult.status === 'error' ? (
            <StatePanel
              variant={stateVariantFor(reorderResult.error.status)}
              title={
                reorderResult.error.status === 403
                  ? 'دسترسی ندارید'
                  : 'گزارش نقاط سفارش قابل نمایش نیست'
              }
            />
          ) : null}

          {reorderResult.status === 'ready' ? (
            <>
              <div className="inv-kpi-grid">
                <div data-tone="danger">
                  <V2Kpi label="کسری بحرانی (زیر حداقل)" value={fa(counts.critical)} />
                </div>
                <div data-tone="warn">
                  <V2Kpi label="نیازمند سفارش مجدد" value={fa(counts.reorder)} />
                </div>
                <div data-tone="info">
                  <V2Kpi label="موجود مازاد بر سقف" value={fa(counts.surplus)} />
                </div>
                <div data-tone="success">
                  <V2Kpi label="موجودی نرمال" value={fa(counts.normal)} />
                </div>
              </div>

              <V2DataGrid
                icon="warn"
                title="جدول کنترل موجودی و نقاط سفارش"
                subtitle={`${fa(reorderResult.data.alerts.length)} قلم تعریف‌شده`}
                columns={reorderColumns}
                rows={reorderResult.data.alerts}
                rowKey={(row) => row.productId}
                rowTone={(row) =>
                  row.status === 'critical'
                    ? 'danger'
                    : row.status === 'reorder'
                      ? 'warn'
                      : undefined
                }
                legend={[
                  { tone: 'danger', label: 'کسری بحرانی' },
                  { tone: 'warn', label: 'نیاز به سفارش' },
                ]}
                storageKey="inventory.reorder-alerts"
                zebra
                emptyState={
                  <V2EmptyState
                    icon="info"
                    title="نقطهٔ سفارشی تعریف نشده است"
                    description="برای کالاها حداقل، نقطهٔ سفارش و حداکثر تعریف کنید تا هشدار نمایش داده شود."
                    compact
                  />
                }
              />
            </>
          ) : null}
        </>
      ) : null}

      {/* TAB 3: Stagnant Stock */}
      {activeTab === 'stagnant' ? (
        <>
          {stagnantResult.status === 'loading' ? (
            <LoadingState label="در حال شناسایی اقلام بدون گردش…" />
          ) : null}
          {stagnantResult.status === 'error' ? (
            <StatePanel
              variant={stateVariantFor(stagnantResult.error.status)}
              title={
                stagnantResult.error.status === 403
                  ? 'دسترسی ندارید'
                  : 'گزارش اقلام راکد قابل نمایش نیست'
              }
            />
          ) : null}

          {stagnantResult.status === 'ready' ? (
            <V2DataGrid
              icon="clock"
              title="اقلام راکد و کم‌گردش انبار"
              subtitle={`${fa(stagnantResult.data.items.length)} قلم بدون گردش در بازه`}
              toolbarExtra={
                <label className="inv-toolbar-field">
                  <span className="inv-form__lead">حداقل بازهٔ بدون خروج:</span>
                  <select
                    className="sk-select inv-toolbar-select"
                    value={stagnantDays}
                    onChange={(e) => setStagnantDays(Number(e.target.value))}
                  >
                    {STAGNANT_DAY_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
              }
              columns={stagnantColumns}
              rows={stagnantResult.data.items}
              rowKey={(item) => item.productId}
              storageKey="inventory.stagnant"
              zebra
              emptyState={
                <V2EmptyState
                  icon="check"
                  title="هیچ کالای راکدی یافت نشد"
                  description="همهٔ کالاهای دارای موجودی در این بازه گردش انبار داشته‌اند."
                  compact
                />
              }
            />
          ) : null}
        </>
      ) : null}
    </div>
  );
}
